'use client';

import * as React from 'react';

/**
 * WEB-767 — the Discover Sidebar layout region (`<aside>`).
 *
 * `DISCOVER_RAIL_STICKY_CLASS` caps height at the *stuck* size
 * (`100dvh − --nav-stick-top − 1rem`). Before the rail reaches that `top`,
 * the box starts lower (under the coord + hero strips) so the same cap
 * still hangs past the fold — the last filters stay off-screen unless the
 * List is scrolled. This wrapper tightens `max-height` to the remaining
 * viewport under the aside's live `top`, so the scrollport is fully on
 * screen at every scroll position. The CSS cap stays as the no-JS ceiling.
 */
export function DiscoverRailScrollport({
  children,
  className,
  ...rest
}: React.ComponentPropsWithoutRef<'aside'>): React.ReactElement {
  const ref = React.useRef<HTMLElement>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const publish = (): void => {
      if (getComputedStyle(el).display === 'none') {
        el.style.maxHeight = '';
        return;
      }
      const top = Math.max(0, el.getBoundingClientRect().top);
      el.style.maxHeight = `${Math.max(0, window.innerHeight - top)}px`;
    };

    publish();
    // Coord + hero above the grid can settle after this effect. Observing
    // only the aside misses a position change that does not resize it
    // (stuck-size cap already applied → ResizeObserver is silent).
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      publish();
      raf2 = requestAnimationFrame(publish);
    });

    window.addEventListener('scroll', publish, { passive: true });
    window.addEventListener('resize', publish);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(el);
    if (el.parentElement) observer?.observe(el.parentElement);
    const template = el.closest('[data-testid="discover-template"]');
    if (template instanceof Element) observer?.observe(template);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      observer?.disconnect();
      window.removeEventListener('scroll', publish);
      window.removeEventListener('resize', publish);
    };
  }, []);

  return (
    <aside ref={ref} className={className} {...rest}>
      {children}
    </aside>
  );
}
